/**
 * PDF Conversion Service
 *
 * Converts the first page of a PDF file to a PNG image.
 * Uses pdfjs-dist (lazy-loaded) for client-side rendering.
 */

/**
 * Check if a file is a PDF based on extension and MIME type.
 */
export const isPdfFile = (file: File): boolean => {
  const ext = file.name.toLowerCase().split('.').pop();
  return ext === 'pdf' || file.type === 'application/pdf';
};

/**
 * Convert the first page of a PDF to a PNG image file.
 * Returns the original file unchanged if it is not a PDF.
 * Lazy-loads pdfjs-dist only when a PDF file is detected.
 */
export const convertPdfToImage = async (file: File, scale: number = 2.0): Promise<File> => {
  if (!isPdfFile(file)) {
    return file;
  }

  console.log(`[PDF] Converting ${file.name} (${file.size} bytes) to PNG...`);

  try {
    const pdfjs = await import('pdfjs-dist');

    pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
    const page = await pdf.getPage(1);

    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context not available');

    await page.render({ canvasContext: ctx, viewport, canvas } as Parameters<typeof page.render>[0]).promise;

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Canvas toBlob failed'))),
        'image/png'
      );
    });

    const pngName = file.name.replace(/\.pdf$/i, '.png');
    const pngFile = new File([blob], pngName, { type: 'image/png' });

    console.log(`[PDF] Converted: ${file.size} -> ${pngFile.size} bytes`);
    return pngFile;
  } catch (error) {
    console.error('[PDF] Conversion failed:', error);
    throw new Error(
      'PDF-Konvertierung fehlgeschlagen. Bitte konvertiere die Datei manuell zu einem Bild.'
    );
  }
};
